/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import { Divider, IconButton, ListItemIcon, ListItemText } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AccountCircleRounded from '@mui/icons-material/AccountCircleRounded';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import React, { useState } from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  selectCustomerSupportEmail,
  selectIsAuthenticated,
  selectPrintMode,
  selectUserData,
  selectWeatherOverlayConfig,
  selectWhatsNewLatestChecksum,
  selectWhatsNewPanelOpen,
  selectWhatsNewUserLastViewedChecksum,
  selectWhatsNewMarkdownContent,
} from '../../../../store';
import { signOut } from '../../../../store/session/actions';
import {
  closeWhatsNewPanel,
  openWhatsNewPanel,
  updateUserLastViewedChecksum,
} from '../../../../store/whatsNew/actions';
import Clock from './Clock';
import DisclaimerButton from './DisclaimerButton';
import HeaderActionsWrapper from './HeaderActionsWrapper';
import FeedbackDialog from './FeedbackDialog';

import { Campaign, LiveHelp, Logout } from '@mui/icons-material';
import MenuButton from './MenuButton';
import WhatsNewPanelMenuButton from '../../whatsNew/WhatsNewPanelMenuButton';
import WhatsNewPanel from '../../whatsNew/WhatsNewPanel.js';
import { areAllowedRolesPresent } from '../../../../utils/RBACUtils';
import { Authorities } from '../../../../shared/entities/user';
import NotificationBadge from './NotificationBadge';

const HeaderActions = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userData = useSelector(selectUserData);

  const dispatch = useDispatch();
  const printMode = useSelector(selectPrintMode, shallowEqual);
  const weatherOverlayConfig = useSelector(selectWeatherOverlayConfig, shallowEqual);
  const customerSupportEmail = useSelector(selectCustomerSupportEmail, shallowEqual);
  const whatsNewLatestChecksum = useSelector(selectWhatsNewLatestChecksum, shallowEqual);
  const whatsNewUserLastViewedChecksum = useSelector(selectWhatsNewUserLastViewedChecksum, shallowEqual);
  const whatsNewMarkdownContent = useSelector(selectWhatsNewMarkdownContent, shallowEqual);
  const whatsNewPanelOpen = useSelector(selectWhatsNewPanelOpen, shallowEqual);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
  const appBuildVersion: string = `${import.meta.env.VITE_BUILD_VERSION}`;
  const appBuildDate: string = `${import.meta.env.VITE_BUILD_DATE}`;

  const handleMenu = (event: React.SyntheticEvent) => {
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenWhatsNewPanel = () => {
    dispatch(openWhatsNewPanel());

    if (userData?.claims.email && whatsNewLatestChecksum) {
      dispatch(updateUserLastViewedChecksum(userData.claims.email, whatsNewLatestChecksum));
    }
  };

  const handleCloseWhatsNewPanel = () => {
    dispatch(closeWhatsNewPanel());
  };

  const open = Boolean(anchorEl);
  if (printMode) {
    return (
      <HeaderActionsWrapper className="appheaderactions">
        <Clock mode="dateTime" />
      </HeaderActionsWrapper>
    );
  }

  const weatherProviderName = get(weatherOverlayConfig, 'providerName', '');
  const weatherProviderLink = get(weatherOverlayConfig, 'providerLink', '');
  const customerSupportLink =
    !isNil(customerSupportEmail) && size(customerSupportEmail) ? `mailto:${customerSupportEmail}` : null;
  const shouldShowWhatsNewUi =
    areAllowedRolesPresent(userData, Authorities.ROLE_WHATS_NEW_PANEL) && !!whatsNewMarkdownContent.trim();
  const shouldShowWhatsNewBadge =
    shouldShowWhatsNewUi && !!whatsNewLatestChecksum && whatsNewLatestChecksum !== whatsNewUserLastViewedChecksum;

  return (
    <HeaderActionsWrapper className="appheaderactions">
      <Clock mode="timeOnly" />

      {isAuthenticated && (
        <>
          <IconButton
            aria-owns={open ? 'menu-appbar' : undefined}
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
            className="appheaderactions__button appheaderactions__button--user"
          >
            <NotificationBadge showBadge={shouldShowWhatsNewBadge}>
              <AccountCircleRounded className="appheaderactions__button--user--icon" />
            </NotificationBadge>
          </IconButton>

          <StyledMenu
            autoFocus={false}
            classes={{ paper: 'menu-paper', list: 'menu-list' }}
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={open}
            onClose={handleClose}
          >
            <StyledUserInfo>
              <AccountCircleRounded sx={{ fontSize: 38, margin: '-3px' }} />
              {userData?.claims.email}
            </StyledUserInfo>

            <StyledDivider />

            <StyledMenuItem
              onClick={() => setOpenFeedbackDialog(true)}
            >
              <StyledListItemIcon>
                <Campaign />
              </StyledListItemIcon>
              <ListItemText>Send feedback</ListItemText>
            </StyledMenuItem>

            {!isNil(customerSupportLink) && (
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  window.open(customerSupportLink, '_self');
                }}
              >
                <StyledListItemIcon>
                  <LiveHelp />
                </StyledListItemIcon>
                <ListItemText>Contact support</ListItemText>
              </StyledMenuItem>
            )}

            {shouldShowWhatsNewUi && (
              <WhatsNewPanelMenuButton
                latestChecksum={whatsNewLatestChecksum}
                userLastViewedChecksum={whatsNewUserLastViewedChecksum}
                onClick={() => {
                  handleClose();
                  handleOpenWhatsNewPanel();
                }}
              />
            )}

            <StyledMenuItem
              onClick={() => {
                handleClose();
                dispatch(signOut());
              }}
            >
              <StyledListItemIcon>
                <Logout />
              </StyledListItemIcon>
              <ListItemText>Sign out</ListItemText>
            </StyledMenuItem>

            <StyledDivider />

            <BottomLinks>
              <MenuTextItem>
                Viasat Unified Portal
                {Boolean(appBuildVersion) && <div className="build__info__text">Version {appBuildVersion}</div>}
                {Boolean(appBuildDate) && <div className="build__info__text">{appBuildDate}</div>}
                <DisclaimerButton />
              </MenuTextItem>

              {Boolean(weatherProviderName) && (
                <MenuTextItem>
                  Weather powered by
                  <MenuButton onClick={() => window.open(weatherProviderLink, '_blank')}>
                    {weatherProviderName}
                  </MenuButton>
                </MenuTextItem>
              )}

              <MenuTextItem>
                Copyright © {new Date().getUTCFullYear()} Viasat, Inc.
                <MenuButton onClick={() => window.open('https://www.viasat.com/legal', '_blank')}>Legal</MenuButton>
              </MenuTextItem>
            </BottomLinks>
          </StyledMenu>

          {shouldShowWhatsNewUi && <WhatsNewPanel open={whatsNewPanelOpen} onClose={handleCloseWhatsNewPanel} />}
        </>
      )}

      <FeedbackDialog
        open={openFeedbackDialog}
        onClose={() => setOpenFeedbackDialog(false)}
      />
    </HeaderActionsWrapper>
  );
};

export default HeaderActions;

const StyledUserInfo = styled.div`
  display: flex;
  margin: 0 8px;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #fff;
`;

const StyledDivider = styled(Divider)`
  border-top: 1px solid #465967;
  margin: 8px 0;
`;

const StyledMenu = styled(Menu)`
  .menu-paper {
    display: flex;
    width: auto;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 8px;

    border-radius: 8px;
    border: 1px solid #465967;
    background: #1c262f;
    box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.18);
  }

  .menu-list {
    width: 100%;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  background: #1c262f;

  padding: 8px 16px;
  align-items: center;
  align-self: stretch;

  :hover {
    background: #32424e;
  }

  span {
    font-family: Source Sans Pro;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
  }

  * {
    color: #9fafbc;
  }
`;

const StyledListItemIcon = styled(ListItemIcon)`
  min-width: 30px !important;
`;

const BottomLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 16px;
  margin-right: 16px;
`;

const MenuTextItem = styled.li`
  color: #9fafbc;
  font-size: 12px;
`;
